<template>
  <div class="flex flex-col gap-1 sm:gap-2 w-full">
    <label
      v-if="label"
      class="text-base font-medium text-gray-700 leading-6"
      :class="labelClass"
    >
      {{ label }}
      <span v-if="required" class="text-red-600">*</span>
    </label>

    <div class="relative">
      <input
        :id="inputId"
        :type="effectiveType"
        :value="modelValue as any"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#d0a444]/20 focus:border-[#d0a444] transition-all duration-200 text-base h-[38px]"
        :class="[
          {
            'border-red-300 focus:border-red-500 focus:ring-red-200':
              !!errorMessage,
            'opacity-60 cursor-not-allowed': disabled,
          },
          // add right padding if toggle is visible
          showToggle ? 'pr-9' : '',
          inputClass,
        ]"
        @input="onInput"
        @blur="$emit('blur')"
      />
      <button
        v-if="showToggle"
        type="button"
        class="absolute inset-y-0 right-2 my-auto h-5 w-5 grid place-items-center rounded-md text-gray-500 hover:text-gray-700 focus:outline-none"
        :aria-label="
          revealed ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
        "
        :title="
          revealed ? 'Masquer le mot de passe' : 'Afficher le mot de passe'
        "
        :disabled="disabled"
        @click="toggleReveal"
      >
        <svg
          v-if="!revealed"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          class="w-4.5 h-4.5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M2.036 12.322a1.012 1.012 0 010-.644C3.423 7.51 7.36 5 12 5c4.64 0 8.577 2.51 9.964 6.678.07.2.07.444 0 .644C20.577 16.49 16.64 19 12 19c-4.64 0-8.577-2.51-9.964-6.678z"
          />
          <circle cx="12" cy="12" r="3.25" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          class="w-4.5 h-4.5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3 3l18 18M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88M6.343 6.343C4.403 7.79 3 10 3 12c0 0 3.75 7.5 9 7.5 1.39 0 2.686-.34 3.828-.94M17.657 17.657C19.597 16.21 21 14 21 12c0 0-3.75-7.5-9-7.5-1.39 0-2.686.34-3.828.94"
          />
        </svg>
      </button>
    </div>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <span v-if="errorMessage" class="text-xs text-red-600">{{
        errorMessage
      }}</span>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null;
    label?: string;
    placeholder?: string;
    type?: string;
    required?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    inputClass?: string;
    labelClass?: string;
    id?: string;
    autocomplete?: string;
    showPasswordToggle?: boolean;
  }>(),
  {
    modelValue: "",
    placeholder: "",
    type: "text",
    required: false,
    disabled: false,
    errorMessage: "",
    inputClass: "",
    labelClass: "",
    id: undefined,
    autocomplete: "off",
    showPasswordToggle: true,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (e: "blur"): void;
}>();

const inputId = computed(() => props.id);
const revealed = ref(false);

const isPassword = computed(() => props.type === "password");
const showToggle = computed(() => props.showPasswordToggle && isPassword.value);
const effectiveType = computed(() =>
  isPassword.value && revealed.value ? "text" : props.type,
);

function toggleReveal() {
  revealed.value = !revealed.value;
}

function onInput(e: Event) {
  const target = e.target as HTMLInputElement;
  emit("update:modelValue", target.value);
}
</script>

<style scoped></style>
